// src/pages/ContactPage.tsx

import { useState } from 'react';
import { StaticPageLayout } from '@/components/StaticPageLayout';
import { Send, Mail, CheckCircle } from 'lucide-react';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // TODO: 実際の送信処理（Firebase Functions や外部APIへ）
    // 現状はメールリンクで代替
    const mailtoLink = `mailto:contact.sidepocket@seaside-lab.com?subject=[${formData.category}] お問い合わせ&body=${encodeURIComponent(
      `お名前: ${formData.name}\nメールアドレス: ${formData.email}\n\n${formData.message}`
    )}`;
    
    window.location.href = mailtoLink;
    
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1000);
  };

  if (isSubmitted) {
    return (
      <StaticPageLayout title="お問い合わせ">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            メールアプリが起動しました
          </h2>
          <p className="text-gray-600 mb-8">
            お使いのメールアプリからお問い合わせ内容を送信してください。
            <br />
            通常2〜3営業日以内にご返信いたします。
          </p>
          <button
            onClick={() => {
              setIsSubmitted(false);
              setFormData({ name: '', email: '', category: '', message: '' });
            }}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            新しいお問い合わせ
          </button>
        </div>
      </StaticPageLayout>
    );
  }

  return (
    <StaticPageLayout title="お問い合わせ">
      <p className="text-gray-600 mb-8">
        sidepocketに関するご質問、ご要望、不具合のご報告などは、以下のフォームよりお問い合わせください。
        通常2〜3営業日以内にご返信いたします。
      </p>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-blue-600" />
          <p className="text-sm text-blue-800">
            直接メールでのお問い合わせ：
            <a 
              href="mailto:contact.sidepocket@seaside-lab.com" 
              className="font-medium underline ml-1"
            >
              contact.sidepocket@seaside-lab.com
            </a>
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* お名前 */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            お名前 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder="山田 太郎"
          />
        </div>

        {/* メールアドレス */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            メールアドレス <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder="example@email.com"
          />
        </div>

        {/* カテゴリ */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
            お問い合わせカテゴリ <span className="text-red-500">*</span>
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            <option value="">選択してください</option>
            <option value="機能について">機能について</option>
            <option value="料金・プランについて">料金・プランについて</option>
            <option value="不具合の報告">不具合の報告</option>
            <option value="機能のリクエスト">機能のリクエスト</option>
            <option value="アカウントについて">アカウントについて</option>
            <option value="その他">その他</option>
          </select>
        </div>

        {/* お問い合わせ内容 */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            お問い合わせ内容 <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            placeholder="お問い合わせ内容をご記入ください"
          />
        </div>

        {/* 送信ボタン */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              送信中...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              送信する
            </>
          )}
        </button>
      </form>

      <p className="text-sm text-gray-500 mt-6">
        ※ お問い合わせいただいた内容は、
        <a href="/privacy" className="text-blue-600 hover:underline">プライバシーポリシー</a>
        に基づき適切に管理いたします。
      </p>
    </StaticPageLayout>
  );
};
